import { useState } from 'react';
import { Link2, Link2Off } from 'lucide-react';
import { Button, Modal, Skeleton, toast } from '../../components/ui';
import {
  useHubProjects,
  useLinkHubProject,
  useUnlinkHubProject,
} from '../hub/api';
import { useOrgSettings } from '../settings/api';
import { HubProjectPicker } from './HubProjectPicker';

/**
 * Link this agreement to a hub project, from the agreement itself.
 *
 * Wave C2 shipped linking in Settings → Hub only, which meant the question a
 * agreement page actually raises — "does the hub know about this one?" — could
 * only be answered by leaving the agreement, opening a settings tab, and reading
 * a table of every agreement in the org.
 *
 * Two sources, deliberately:
 *  - **linked or not** comes from our own `Agreement.hubProjectId`, so the answer
 *    survives the hub being unreachable (it is our column, not their API);
 *  - the hub project's **name** and the picker's rows come from
 *    `GET /api/hub/projects`, which degrades to "not connected" without ever
 *    breaking this page.
 */
export function AgreementHubLink({
  agreementId,
  agreementName,
  hubProjectIds,
}: {
  agreementId: string;
  agreementName: string;
  hubProjectIds: string[];
}) {
  const hub = useHubProjects();
  const link = useLinkHubProject();
  const unlink = useUnlinkHubProject();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [choice, setChoice] = useState('');

  const rows = hub.data?.projects ?? [];
  const requireHubLink = useOrgSettings().data?.requireHubLink === true;
  const connected = hub.data?.connected ?? false;
  const linkedRows = hubProjectIds.map(
    (id) => rows.find((r) => r.hubProjectId === id) ?? { hubProjectId: id, name: null as string | null, archivedInHub: false },
  );

  if (hub.isPending) return <Skeleton className="mt-2 h-6 w-56" />;
  // Hub turned off (or unreachable) and nothing linked: say nothing rather than
  // offering a button that cannot work. A link we already hold still renders.
  if (hubProjectIds.length === 0 && !connected) return null;

  const linkedList = linkedRows.map((row) => (
    <p
      key={row.hubProjectId}
      className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-body-sm text-grey-500"
    >
      <span className="flex min-w-0 items-start gap-2">
        <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-accent-700" aria-hidden />
        <span>
          Linked to hub project{' '}
          <span className="font-semibold text-secondary">
            {row.name ? `“${row.name}”` : row.hubProjectId}
          </span>
          {row.archivedInHub ? ' · archived in hub' : ''}
        </span>
      </span>
      <Button
        variant="ghost"
        size="sm"
        loading={unlink.isPending}
        onClick={() =>
          unlink.mutate(
            { hubProjectId: row.hubProjectId, agreementId },
            {
              onSuccess: () => toast.success('Unlinked from the hub project'),
              onError: (error) =>
                toast.error(error instanceof Error ? error.message : 'Could not unlink'),
            },
          )
        }
      >
        <Link2Off className="h-4 w-4" aria-hidden />
        Unlink
      </Button>
    </p>
  ));

  return (
    <>
      {linkedList}
      {linkedRows.length > 0 ? (
        // One agreement can fund SEVERAL hub projects (two hub projects feeding
        // off one support agreement, 2026-08-23) — so linked is never final.
        connected && (
          <div className="mt-1">
            <Button variant="ghost" size="sm" onClick={() => setPickerOpen(true)}>
              <Link2 className="h-4 w-4" aria-hidden />
              Link another hub project
            </Button>
          </div>
        )
      ) : requireHubLink ? (
        // The org POLICY says every agreement carries a hub project. A bare
        // one is not an error state — the Harvest sync lands rows unlinked —
        // but it is unfinished, and the screen says so where the fix lives.
        <p className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-status-late-bg px-3 py-2 text-body-sm text-status-late">
          <span>
            <strong>Not linked to a hub project</strong> — this organization requires every
            agreement to be linked.
          </span>
          <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
            <Link2 className="h-4 w-4" aria-hidden />
            Link now
          </Button>
        </p>
      ) : (
        <div className="mt-2">
          <Button variant="ghost" size="sm" onClick={() => setPickerOpen(true)}>
            <Link2 className="h-4 w-4" aria-hidden />
            Link to hub project
          </Button>
        </div>
      )}

      <Modal
        open={pickerOpen}
        onOpenChange={(open) => {
          if (!open) {
            setChoice('');
            setPickerOpen(false);
          }
        }}
        title="Link to a hub project"
        description={`“${agreementName}” will keep every money field it has.`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPickerOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!choice}
              loading={link.isPending}
              onClick={() =>
                link.mutate(
                  { hubProjectId: choice, agreementId },
                  {
                    onSuccess: () => {
                      toast.success('Linked to the hub project');
                      setChoice('');
                      setPickerOpen(false);
                    },
                    onError: (error) =>
                      toast.error(
                        error instanceof Error ? error.message : 'Could not link the agreement',
                      ),
                  },
                )
              }
            >
              <Link2 className="h-4 w-4" aria-hidden />
              Link
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <HubProjectPicker
            rows={rows}
            agreementId={agreementId}
            value={choice}
            onChange={setChoice}
          />
          <p className="text-body-sm text-grey-500">
            A hub project already linked to another agreement can take this one too — several
            support agreements draw down one hub project in sequence, rolling over when one runs
            out. The hub owns the name and archived state; financials owns type, rates and budgets
            — linking records the connection only.
          </p>
        </div>
      </Modal>
    </>
  );
}
